import {
  getCoreRollCandidates,
  migrateCoreAttackStyles,
} from './coreAttackStyles';
import { isCoreDamageRoll } from './coreDamage';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GiVortex } from 'react-icons/gi';
import RollReveal from './RollReveal';
import HealingFeedback from './HealingFeedback';
import HapticsToggle from './HapticsToggle';
import RunStatus from './RunStatus';
import { getRewardDisplay } from './rewardCollectionTiming';
import { CoreboundHelpProvider } from './CoreboundHelp';
import CoreboundNavButton from './CoreboundNavButton';
import CoreboundIcon from './CoreboundIcon';
import { getSectorJourney } from './sectorJourney';
import CoreboundOpening from './CoreboundOpening';
import RelicGlyph from './RelicGlyph';
import { migrateRechargeChoice } from './boardTileEffects';
import PortalChamber from './PortalChamber';
import CoreAccess from './CoreAccess';
import { getRouteOutcome, validPortalRoutes } from './portalRoutes';
import BoardConditionNotice, {
  BoardConditionBadge,
} from './BoardConditionNotice';
import {
  getRoomBoardCondition,
  normalizeBoardCondition,
} from './boardConditions';
import LastStandProtocolRoll from './LastStandProtocolRoll';
import DefeatSequence from './DefeatSequence';
import VictorySequence from './VictorySequence';
import EncounterLootSequence from './EncounterLootSequence';
import GuardianBattle from './GuardianBattle';
import GuardianDamageCalculation from './GuardianDamageCalculation';
import { migrateGuardianGame, startGuardianAttack } from './guardianCombat';
import {
  MINI_BOSS_PHASES,
  MINI_BOSS_SECTOR,
  isMiniBossSaveValid,
  GUARDIAN_PHASES,
  isGuardianSaveValid,
} from './sectorEnemies';
import { activatePhaseAnchor } from './phaseAnchor';
import { clearBountyTag, markBountyTile } from './bountyTag';
import { recordThrowEvent } from './throwCombo';
import { getBoardEffectTotal, toggleChainReaction } from './chainReaction';
import {
  activatePortalPair,
  createEmptyPortalPair,
  isPortalSector,
} from './portalPair';
import { normalizeSlingshotLaunch } from './slingshot';
import RelicActivation from './RelicActivation';
import { usePopupTransition } from './popupTransition';
import RelicStoreOffers from './RelicStoreOffers';
import RepairButtonAura from './RepairButtonAura';
import DoorIcon from './DoorIcon';
import TreasureChestIcon from './TreasureChestIcon';
import GameGuide from './GameGuide';
import { getDoorReward, getDoorType } from './doors';
import {
  DOOR_BOARD_VERSION,
  migratePlayerBoardBalance,
  BOSS_BOARD_VERSION,
  generateDoorBoard,
  generateBossDoorBoard,
  getBoardSquareImpactLabel,
} from './doorBoard';
import BossBattle, { SparkButton, Victory } from './BossBattle';
import {
  BOSS_MAX_HP,
  BOSS_RELIC_NOTES,
  STANCES,
  getCorePhase,
  getCorePhaseForHp,
  getBossVictoryDie,
  beginBoss,
  nextBossRound,
  resolveBossRound,
  migrateBossDamage,
  migrateRetiredBossTiles,
  getBossIntent,
  buyRelic,
} from './boss';
import {
  getRelicPrice,
  getShopTier,
  prepareShop,
  getShopPurchaseId,
} from './shop';
import {
  RELIC_REROLL_COST,
  getRepairCost,
  RELICS,
  beginRoomRoll,
  chooseRelic,
  chooseRoom,
  continueRun,
  commitRoomRoll,
  createGame,
  enterBreach,
  flipRoll,
  finishBoardTileEffects,
  prepareRelicOffers,
  getEffectiveRoll,
  getMaxHp,
  getLandingHealingDisplay,
  getRelicOptions,
  getRooms,
  getRecentRooms,
  rerollRelicOptions,
  prepareDoors,
  prepareDoorBoard,
  validDoorOffers,
  repair,
  resolveRoll,
  resolveLastStandRoll,
  resolveBoardTileChoice,
  resolveMiniBossAttack,
  startRun,
} from './game';
import { REPAIR_DESCRIPTION } from './economy';
import './Corebound.css';
import './BossBattle.css';
import { playSound } from '../../utils/utils';
import { formatDieValue, getDie, selectDie } from './dice';
import { isValidPoweredRoll, migratePowerUpTiles } from './powerUp';
import { sabotageCoreBoard } from './boardTileEffects';
import { migrateCoreBoardPhases } from './coreBoardPhases';
import { getTotalShardsAcquired } from './shardTotals';
import { CORE_SECTOR, TOTAL_SECTORS } from './runConstants';
import './CoreboundViewport.css';
import './CoreboundHud.css';
import { usesPlainHealthBars } from './healthBarPresentation';

const STORAGE_KEY = 'slashRoll.corebound.v1';
// Keep saved runs written with the prior storage key available after renaming.
const LEGACY_STORAGE_KEY = 'slashRoll.glitchTower.v1';
const PHASES = [
  'intro',
  'opening',
  'relic',
  'choose',
  'ready',
  'rolled',
  'result',
  'won',
  'lost',
  'shop',
  'bossReady',
  'bossPlayerRolled',
  'bossRolled',
  'bossResult',
  ...MINI_BOSS_PHASES,
  ...GUARDIAN_PHASES,
];

const normalizePortalPair = (pair) => {
  if (pair?.used !== true && pair?.used !== false)
    return createEmptyPortalPair();
  const validEntry =
    Number.isInteger(pair?.entryIndex) &&
    pair.entryIndex >= 0 &&
    pair.entryIndex < 25;
  const validExit =
    Number.isInteger(pair?.exitIndex) &&
    pair.exitIndex >= 0 &&
    pair.exitIndex < 25 &&
    pair.exitIndex !== pair.entryIndex;
  return validEntry && validExit
    ? {
        entryIndex: pair.entryIndex,
        exitIndex: pair.exitIndex,
        used: pair.used === true,
      }
    : createEmptyPortalPair();
};

const loadGame = () => {
  try {
    const saved = JSON.parse(
      localStorage.getItem(STORAGE_KEY) ??
        localStorage.getItem(LEGACY_STORAGE_KEY)
    );
    const migrated = saved ? migratePowerUpTiles(saved) : saved;
    // Retired catalog entries must not invalidate an otherwise valid run.
    if (Array.isArray(migrated?.relics))
      migrated.relics = migrated.relics.filter((id) => !!RELICS[id]);
    const oldBossEncounter =
      migrated?.floor === 5 &&
      ([
        'shop',
        'bossReady',
        'bossPlayerRolled',
        'bossRolled',
        'bossResult',
      ].includes(migrated.phase) ||
        migrated.roomId === 'boss' ||
        migrated.lastResult?.boss === true);
    const oldLegacyCoreArrival =
      migrated?.floor === 5 &&
      migrated.guardianCombatVersion !== 1 &&
      !migrated.portalRun &&
      ['choose', 'ready', 'rolled', 'result'].includes(migrated.phase);
    const oldCoreSave = oldBossEncounter || oldLegacyCoreArrival;
    const stored = oldCoreSave ? { ...migrated, floor: CORE_SECTOR } : migrated;
    if (
      stored &&
      PHASES.includes(stored.phase) &&
      isMiniBossSaveValid(stored) &&
      isGuardianSaveValid(stored) &&
      (!MINI_BOSS_PHASES.includes(stored.phase) ||
        (getRooms(stored).some((room) => room.id === stored.roomId) &&
          (stored.miniBoss.offers || []).every(
            (id) => RELICS[id] && !stored.relics.includes(id)
          ))) &&
      Number.isInteger(stored.floor) &&
      stored.floor >= 1 &&
      stored.floor <= TOTAL_SECTORS &&
      Number.isInteger(stored.hp) &&
      stored.hp >= 0 &&
      (stored.maxHp === undefined
        ? stored.hp <= 12
        : Number.isInteger(stored.maxHp) &&
          stored.maxHp >= 10 &&
          stored.hp <= stored.maxHp) &&
      Number.isInteger(stored.shards) &&
      stored.shards >= 0 &&
      Array.isArray(stored.relics) &&
      stored.relics.every((id) => RELICS[id]) &&
      Array.isArray(stored.history) &&
      (!stored.portalRun ||
        (stored.floor === 1
          ? stored.breachIntro
          : stored.floor === CORE_SECTOR || validPortalRoutes(stored))) &&
      (!stored.doorOffers || validDoorOffers(stored)) &&
      (!['ready', 'minibossReady', 'guardianReady', 'guardianStrike'].includes(
        stored.phase
      ) ||
        getRooms(stored).some((room) => room.id === stored.roomId)) &&
      (!['rolled', 'minibossRolled', 'guardianRolled'].includes(stored.phase) ||
        (getRooms(stored).some((room) => room.id === stored.roomId) &&
          (stored.roll === null ||
            isValidPoweredRoll(
              stored.roll,
              stored.selectedDie,
              stored.powerUp
            )))) &&
      (!['result', 'won', 'lost'].includes(stored.phase) || stored.lastResult)
    ) {
      delete stored.bestRoll;
      delete stored.runBestRoll;
      const loaded = prepareRelicOffers(
        prepareDoorBoard(
          prepareDoors({
            ...createGame(),
            ...stored,
            totalShardsAcquired: getTotalShardsAcquired(stored),
            // Preserve the 12-HP cap of runs saved before expandable health.
            maxHp: stored.maxHp ?? 12,
            history: getRecentRooms(stored.history),
          })
        )
      );
      loaded.chainReaction = {
        armed: loaded.chainReaction?.armed === true,
        used:
          !!loaded.chainReaction?.used ||
          Number.isInteger(loaded.chainReaction?.usedFloor),
        usedFloor: loaded.chainReaction?.usedFloor ?? null,
      };
      if (oldBossEncounter) {
        const moveOldCoreFloor = (record) =>
          record?.floor === 5 ? { ...record, floor: CORE_SECTOR } : record;
        if (loaded.marketPurchased === 5) loaded.marketPurchased = CORE_SECTOR;
        if (loaded.phaseAnchor?.usedFloor === 5)
          loaded.phaseAnchor = {
            ...loaded.phaseAnchor,
            usedFloor: CORE_SECTOR,
          };
        if (loaded.chainReaction?.usedFloor === 5)
          loaded.chainReaction = {
            ...loaded.chainReaction,
            usedFloor: CORE_SECTOR,
          };
        if (loaded.chainReactionActivation?.floor === 5)
          loaded.chainReactionActivation = {
            ...loaded.chainReactionActivation,
            floor: CORE_SECTOR,
          };
        if (loaded.boardTileResolution?.floor === 5)
          loaded.boardTileResolution = {
            ...loaded.boardTileResolution,
            floor: CORE_SECTOR,
          };
        if (loaded.throwCombo?.floor === 5)
          loaded.throwCombo = {
            ...loaded.throwCombo,
            floor: CORE_SECTOR,
          };
        loaded.lastResult = moveOldCoreFloor(loaded.lastResult);
        loaded.history = loaded.history.map((entry) =>
          entry.boss ? moveOldCoreFloor(entry) : entry
        );
      }
      loaded.relics = loaded.relics.filter((id) => id !== 'portalPair');
      Object.assign(loaded, migrateRechargeChoice(loaded));
      loaded.corePhase = getCorePhase(loaded);
      loaded.conditionSeed = Number.isInteger(stored.conditionSeed)
        ? stored.conditionSeed
        : 0;
      loaded.boardCondition =
        loaded.roomId === 'boss' || loaded.phase.startsWith('boss')
          ? null
          : normalizeBoardCondition(stored.boardCondition);
      loaded.selectedDie = getDie(stored.selectedDie).id;
      Object.assign(loaded, migratePlayerBoardBalance(loaded));
      if (
        loaded.bossRoundState?.round === loaded.bossRound &&
        !loaded.bossRoundState.corePhaseBefore
      ) {
        loaded.bossRoundState = {
          ...loaded.bossRoundState,
          corePhaseBefore: getCorePhaseForHp(
            loaded.bossRoundState.bossHpBefore
          ),
        };
      }
      if (
        ['bossReady', 'bossPlayerRolled', 'bossRolled', 'bossResult'].includes(
          loaded.phase
        ) &&
        (!Array.isArray(loaded.doorBoard) ||
          loaded.doorBoard.length !== 25 ||
          (loaded.phase === 'bossReady' &&
            loaded.doorBoardVersion !== DOOR_BOARD_VERSION))
      ) {
        loaded.doorBoard = generateDoorBoard(Math.random, { core: true });
        loaded.portalPair = createEmptyPortalPair();
        loaded.doorBoardVersion = DOOR_BOARD_VERSION;
      }
      Object.assign(loaded, migrateRetiredBossTiles(loaded));
      if (
        ['bossReady', 'bossPlayerRolled', 'bossRolled', 'bossResult'].includes(
          loaded.phase
        ) &&
        (!Array.isArray(loaded.bossDoorBoard) ||
          loaded.bossDoorBoard.length !== 25 ||
          loaded.bossDoorBoardVersion !== BOSS_BOARD_VERSION)
      ) {
        if (loaded.bossDoorBoard?.length === 25) {
          // Preserve the board layout and sabotage; retired effect slots
          // become the current charged attack without retaining old rules.
          const supportedEffects = [
            'critical',
            'criticalMiss',
            'shield',
            'massiveAttack',
            'corePower',
          ];
          loaded.bossDoorBoard = loaded.bossDoorBoard.map((square) =>
            square.effect && !supportedEffects.includes(square.effect.type)
              ? {
                  modifier: 0,
                  coreDamage: true,
                  effect: { type: 'massiveAttack' },
                }
              : square
          );
        } else {
          loaded.bossDoorBoard = generateBossDoorBoard();
        }
        loaded.bossDoorBoardVersion = BOSS_BOARD_VERSION;
      }
      Object.assign(loaded, migrateBossDamage(loaded));
      Object.assign(loaded, migrateCoreBoardPhases(loaded));
      const styled = migrateCoreAttackStyles(loaded);
      delete loaded.charge;
      delete loaded.burst;
      delete loaded.burstCost;
      Object.assign(loaded, styled);
      const oldSabotage = loaded.boardTileResolution?.pending;
      if (oldSabotage?.type === 'sabotage' && !oldSabotage.cutscene) {
        const { board, changes } = sabotageCoreBoard(loaded.bossDoorBoard);
        if (changes.length) {
          const criticalCount = changes.filter((change) =>
            change.categories.includes('critical')
          ).length;
          const modifierCount = changes.filter((change) =>
            change.categories.includes('modifier')
          ).length;
          loaded.bossDoorBoard = board;
          loaded.boardTileResolution = {
            ...loaded.boardTileResolution,
            pending: {
              ...oldSabotage,
              cutscene: true,
              changes,
              criticalCount,
              modifierCount,
              options: [],
            },
            logs: [
              ...(loaded.boardTileResolution.logs || []),
              `Sabotage turned ${criticalCount} Critical Hit tiles into Critical Misses and reversed ${modifierCount} positive modifiers on the Core’s next board.`,
            ],
          };
        }
      }
      if (Array.isArray(loaded.doorBoard) && loaded.doorBoard.length === 25) {
        loaded.portalPair = isPortalSector(loaded.floor)
          ? normalizePortalPair(loaded.portalPair)
          : createEmptyPortalPair();
      }
      const pendingSurvival = loaded.lastStandProtocol?.pending;
      if (
        Array.isArray(pendingSurvival?.doorBoard) &&
        pendingSurvival.doorBoard.length === 25
      ) {
        loaded.lastStandProtocol = {
          ...loaded.lastStandProtocol,
          pending: {
            ...pendingSurvival,
            portalPair: createEmptyPortalPair(),
          },
        };
      }
      // Older saves reached the Core as an ordinary room. Send those runs to
      // the new preparation shop, preserving their HP, relics and currency.
      if (
        !loaded.portalRun &&
        loaded.floor === CORE_SECTOR &&
        ['choose', 'ready', 'rolled', 'result'].includes(loaded.phase)
      ) {
        return prepareShop({
          ...loaded,
          phase: 'shop',
          roomId: null,
          roll: null,
        });
      }
      if (
        [
          'shop',
          'bossReady',
          'bossPlayerRolled',
          'bossRolled',
          'bossResult',
        ].includes(loaded.phase)
      ) {
        const pendingRound =
          loaded.bossRoundState?.round === loaded.bossRound
            ? loaded.bossRoundState
            : null;
        const pendingCoreDefeat =
          ['bossPlayerRolled', 'bossRolled'].includes(loaded.phase) &&
          (pendingRound?.playerImpactApplied ||
            pendingRound?.coreSelfImpactApplied);
        const pendingPlayerDefeat =
          loaded.phase === 'bossRolled' && pendingRound?.counterImpactApplied;
        if (
          loaded.floor !== CORE_SECTOR ||
          (loaded.hp === 0 && !pendingPlayerDefeat) ||
          !Number.isInteger(loaded.bossHp) ||
          loaded.bossHp < (pendingCoreDefeat ? 0 : 1) ||
          loaded.bossHp > BOSS_MAX_HP ||
          !Number.isInteger(loaded.bossRound) ||
          loaded.bossRound < 1 ||
          !STANCES[loaded.stance] ||
          (loaded.phase === 'bossRolled' &&
            (loaded.roomId !== 'boss' ||
              !isValidPoweredRoll(
                loaded.roll,
                loaded.selectedDie,
                loaded.powerUp,
                getCoreRollCandidates(loaded)
              ) ||
              !isCoreDamageRoll(
                loaded.bossRoll,
                getBossIntent(loaded).faces
              ))) ||
          (loaded.phase === 'bossResult' && !loaded.lastResult?.boss)
        )
          return createGame(loaded);
        // An impact can save zero HP while its defeat animation is still playing.
        if (
          (pendingPlayerDefeat && loaded.hp === 0) ||
          (pendingCoreDefeat && loaded.bossHp === 0)
        ) {
          return resolveBossRound(loaded);
        }
      }
      const resumed = [
        'rolled',
        'guardianRolled',
        'bossPlayerRolled',
        'bossRolled',
      ].includes(loaded.phase)
        ? finishBoardTileEffects(loaded)
        : loaded;
      return migrateGuardianGame(prepareShop(nextBossRound(resumed)));
    }
  } catch {
    // A damaged or unavailable local save should not prevent a new run.
  }
  return createGame();
};

const Corebound = () => {
  useEffect(() => {
    // Retire the compatibility experiment, including preferences left by a
    // previous deployed build. 3D remains the default after any reload.
    try {
      sessionStorage.removeItem('slashRoll.corebound.graphics.active.v1');
      sessionStorage.removeItem('slashRoll.corebound.graphics.mode.v1');
    } catch {
      // Storage can be blocked; these old preferences are no longer read.
    }
    delete document.documentElement.dataset.coreboundGraphics;
    delete document.documentElement.dataset.coreboundGraphicsDocument;
    const url = new URL(window.location.href);
    if (url.searchParams.has('coreboundGraphics')) {
      url.searchParams.delete('coreboundGraphics');
      window.history.replaceState(window.history.state, '', url);
    }
  }, []);
  const history = useHistory();
  const [game, setGame] = useState(loadGame);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const victoryDie = useMemo(
    () => (game.phase === 'won' ? getBossVictoryDie(game) : null),
    [game]
  );
  const protocolPending = !!game.lastStandProtocol?.pending;
  const protocolPendingRef = useRef(protocolPending);
  const maxHp = getMaxHp(game);
  const repairCost = getRepairCost(game);
  const [showRollReveal, setShowRollReveal] = useState(
    () => game.phase === 'rolled' && game.roll !== null
  );
  const [restoreCompletedRoll, setRestoreCompletedRoll] = useState(
    () => game.phase === 'rolled' && game.roll !== null
  );
  const [rollCharge, setRollCharge] = useState(() =>
    game.slingshotLaunch
      ? normalizeSlingshotLaunch(game.slingshotLaunch).power
      : 0.72
  );
  const [rollLaunch, setRollLaunch] = useState(() =>
    game.slingshotLaunch ? normalizeSlingshotLaunch(game.slingshotLaunch) : null
  );
  const [acquiredRelic, setAcquiredRelic] = useState(null);
  const purchaseId = getShopPurchaseId(game);
  const purchaseReceipt = purchaseId
    ? {
        id: purchaseId,
        message: `${RELICS[purchaseId].name} installed for ${
          game.floor === CORE_SECTOR
            ? 'the final battle'
            : 'the rest of this run'
        }`,
      }
    : null;
  const [repairFeedback, setRepairFeedback] = useState(null);
  const [shopExitingIds, setShopExitingIds] = useState([]);
  const [guideQuery, setGuideQuery] = useState(null);
  const [quitDialogOpen, setQuitDialogOpen] = useState(false);
  const { close: closeQuitDialog, popupClass: quitPopupClass } =
    usePopupTransition(() => setQuitDialogOpen(false), {
      open: quitDialogOpen,
    });
  const guideButtonRef = useRef(null);
  const quitDialogRef = useRef(null);
  const quitCancelRef = useRef(null);
  const guideOpenRef = useRef(false);
  const successfulResultSoundRef = useRef(null);
  guideOpenRef.current = guideQuery !== null;
  const mainRef = useRef(null);
  const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;
  const [completedDefeatResult, setCompletedDefeatResult] = useState(null);
  const defeatSequenceComplete =
    game.phase !== 'lost' ||
    reduceMotion ||
    completedDefeatResult === game.lastResult;
  const defeatSequenceActive = game.phase === 'lost' && !defeatSequenceComplete;
  const [completedVictoryResult, setCompletedVictoryResult] = useState(null);
  const victorySequenceComplete =
    game.phase !== 'won' ||
    reduceMotion ||
    game.lastResult?.liberationSeen === true ||
    completedVictoryResult === game.lastResult;
  const victorySequenceActive =
    game.phase === 'won' && !victorySequenceComplete;
  const [completedEncounterResult, setCompletedEncounterResult] =
    useState(null);
  const encounterSequenceActive =
    game.phase === 'result' &&
    !!game.lastResult?.enemy &&
    completedEncounterResult !== game.lastResult;
  const [rewardProgress, setRewardProgress] = useState(null);
  const [combatPresentation, setCombatPresentation] = useState(null);
  const rewardHealthRef = useRef(null);
  const rewardShardsRef = useRef(null);
  const rewardResult =
    game.phase === 'guardianStrike'
      ? game.guardian.lastRound
      : encounterSequenceActive
      ? game.lastResult
      : null;
  const rewardDisplay = getRewardDisplay(game, rewardResult, rewardProgress);
  const landingHealing = getLandingHealingDisplay(game);
  const shownCombatStage =
    combatPresentation && combatPresentation.id === rewardResult?.combatId
      ? combatPresentation.stage
      : 'launch';
  const healthDisplay = protocolPending
    ? {
        ...rewardDisplay,
        hp:
          game.lastStandProtocol.pending.impactSeen ||
          game.lastStandProtocol.pending.stage !== 'ready'
            ? 0
            : game.lastStandProtocol.pending.hpBefore,
      }
    : rewardResult?.retaliation && shownCombatStage !== 'counter'
    ? {
        ...rewardDisplay,
        shield: getBoardEffectTotal(game, 'shield'),
        hp: Math.max(
          0,
          rewardResult.playerHpBefore +
            (rewardResult.healedOnLanding || 0) -
            (rewardResult.boardDamage || 0)
        ),
      }
    : landingHealing || rewardDisplay;
  const rewardHudActive =
    !protocolPending &&
    (game.phase === 'guardianStrike' || encounterSequenceActive);
  const boardActive =
    protocolPending ||
    encounterSequenceActive ||
    [
      'ready',
      'rolled',
      'bossReady',
      'bossPlayerRolled',
      'bossRolled',
      'bossResult',
      ...MINI_BOSS_PHASES,
      ...GUARDIAN_PHASES,
    ].includes(game.phase);
  const hideBoardStats =
    boardActive &&
    !(
      rewardHudActive &&
      rewardResult?.success &&
      shownCombatStage !== 'launch'
    );

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(game));
    } catch {
      // The current run can still be played if browser storage is unavailable.
    }
  }, [game]);

  useEffect(() => {
    if (protocolPending && !protocolPendingRef.current)
      playSound('/sounds/last-stand.mp3', { preventOverlap: true });
    protocolPendingRef.current = protocolPending;
  }, [protocolPending]);

  useEffect(() => {
    if (!quitDialogOpen) return undefined;
    const dialog = quitDialogRef.current;
    if (dialog?.showModal) dialog.showModal();
    else dialog?.setAttribute('open', '');
    quitCancelRef.current?.focus({ preventScroll: true });
    return () => {
      if (dialog?.open) dialog.close?.();
    };
  }, [quitDialogOpen]);

  useEffect(() => {
    if (game.phase !== 'won' || !victorySequenceComplete) return;
    playSound('/sounds/boss_defeated_victory.mp3', { preventOverlap: true });
  }, [game.phase, victorySequenceComplete]);

  useEffect(() => {
    if (game.phase !== 'lost') return;
    playSound('/sounds/boss_killed_player.mp3', { preventOverlap: true });
  }, [game.phase]);

  useEffect(() => {
    if (
      game.phase !== 'result' ||
      !game.lastResult?.success ||
      encounterSequenceActive ||
      successfulResultSoundRef.current === game.lastResult
    )
      return;
    successfulResultSoundRef.current = game.lastResult;
    playSound('/sounds/treasure_chest_reveal_2.mp3', { preventOverlap: true });
  }, [game.phase, game.lastResult, encounterSequenceActive]);

  useEffect(() => {
    if (
      defeatSequenceActive ||
      victorySequenceActive ||
      encounterSequenceActive
    )
      return;
    if (guideOpenRef.current || protocolPending) return;
    const panel = mainRef.current?.querySelector('.coreboundPanel');
    const heading = panel?.querySelector('h2');
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
    if (
      panel &&
      (panel.getBoundingClientRect().top < 0 ||
        panel.getBoundingClientRect().top > window.innerHeight / 2)
    ) {
      panel.scrollIntoView?.({ block: 'start', behavior: 'auto' });
    }
  }, [
    game.phase,
    protocolPending,
    defeatSequenceActive,
    victorySequenceActive,
    encounterSequenceActive,
  ]);

  useEffect(() => {
    if (game.phase !== 'shop') setShopExitingIds([]);
  }, [game.phase]);

  useEffect(() => {
    if (game.phase !== 'shop') return;
    const offers = (game.shopOffers || []).filter(
      (id) => !game.relics.includes(id)
    );
    if (offers.some((id) => game.shards >= getRelicPrice(game, id))) return;

    setShopExitingIds((current) => [...new Set([...current, ...offers])]);
  }, [game]);

  const room = getRooms(game).find((item) => item.id === game.roomId);
  const effectiveRoll = getEffectiveRoll(game);
  const inBattle = [
    'bossReady',
    'bossPlayerRolled',
    'bossRolled',
    'bossResult',
  ].includes(game.phase);
  const startRoomRoll = (launch) => {
    setRollCharge(launch.power);
    setRollLaunch(launch);
    setShowRollReveal(true);
    setRestoreCompletedRoll(false);
    setGame((current) => ({
      ...beginRoomRoll(current, current.roomId),
      slingshotLaunch: launch,
    }));
  };

  const finishRoomRoll = (roll, landingIndex, relaunchId, faceValues) => {
    setGame((current) =>
      commitRoomRoll(
        current,
        roll,
        landingIndex,
        Math.random,
        relaunchId,
        faceValues
      )
    );
  };

  const takeRelic = (id) => {
    const nextGame = chooseRelic(game, id);
    if (nextGame === game) return;
    playSound('/sounds/relic-obtained.mp3');
    setAcquiredRelic({
      id,
      message: `${RELICS[id].name} installed for the rest of this run`,
    });
    setRepairFeedback(null);
    setGame(nextGame);
  };

  const rerollRelics = () => {
    if (game.phase !== 'relic' || game.shards < RELIC_REROLL_COST) return;
    playSound('/sounds/menu-select-2.mp3');
    setGame((current) => rerollRelicOptions(current));
  };

  const selectRoom = (id, { entrySound = true } = {}) => {
    if (entrySound)
      playSound('/sounds/die-pre-entering-portal.mp3', {
        preventOverlap: true,
      });
    setAcquiredRelic(null);
    setRepairFeedback(null);
    setShowRollReveal(false);
    setRestoreCompletedRoll(false);
    setGame((current) => chooseRoom(current, id));
  };

  const startNewRun = () => {
    playSound('/sounds/transition-to-next-page.mp3');
    setRestoreCompletedRoll(false);
    setShowRollReveal(false);
    setGame(startRun);
  };

  const finishOpening = () => {
    setGame(enterBreach);
  };

  const resetRun = () => {
    const freshGame = createGame(game);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(freshGame));
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // The reset remains available when browser storage is unavailable.
    }
    setShowRollReveal(false);
    setRestoreCompletedRoll(false);
    setRollCharge(0.72);
    setRollLaunch(null);
    setAcquiredRelic(null);
    setRepairFeedback(null);
    setShopExitingIds([]);
    setGuideQuery(null);
    setQuitDialogOpen(false);
    setGame(freshGame);
  };

  const quitRun = () => {
    resetRun();
    history.push('/');
  };

  const startNewGame = () => {
    resetRun();
    history.push('/single-player/corebound');
  };

  const handleRepair = () => {
    if (game.shards < repairCost) return;
    playSound('/sounds/success.mp3');
    setRepairFeedback((current) => ({
      key: (current?.key || 0) + 1,
      healed: 3,
      maxHpGained: Math.max(0, game.hp + 3 - maxHp),
    }));
    setGame(repair);
  };

  const buyBossUpgrade = (id) => {
    const nextGame = buyRelic(game, id);
    if (nextGame === game) return;

    const remainingOffers = (game.shopOffers || []).filter(
      (offerId) => offerId !== id && !nextGame.relics.includes(offerId)
    );
    const exitingIds = [id, ...remainingOffers];

    playSound('/sounds/relic-obtained.mp3');
    setShopExitingIds((current) => [...new Set([...current, ...exitingIds])]);
    setGame(nextGame);
    setAcquiredRelic({
      id,
      message: `${RELICS[id].name} installed for the final battle`,
    });
    setRepairFeedback(null);
  };

  const renderRelicCard = (id, action, children, type = 'RELIC') => {
    const relic = RELICS[id];
    const category = relic.category || relic.rarity;
    return (
      <>
        <span
          className={`coreboundRelicCardIcon coreboundRelicIcon-${relic.animation}`}
          aria-hidden="true"
        >
          <RelicGlyph relicId={relic.id} icon={relic.icon} />
        </span>
        <span
          className={`coreboundRelicRarity coreboundRarity-${category.toLowerCase()}`}
        >
          {category}
        </span>
        <span className="coreboundChoiceType">
          {type}
          {relic.id === 'mirrorChip' || relic.id === 'lastStandProtocol'
            ? ' · REACTIVE'
            : relic.activeAbility
            ? ' · ACTIVE'
            : ' · PASSIVE'}
        </span>
        <strong>{relic.name}</strong>
        <span>{children || relic.description}</span>
        <span className="coreboundChoiceAction">{action}</span>
      </>
    );
  };

  return (
    <main
      data-phase={game.phase}
      data-sector={game.floor}
      style={getSectorJourney(game.floor).style}
      className={`coreboundPage ${usesPlainHealthBars() ? 'has-plain-health' : ''} ${
        game.phase !== 'intro' ? 'coreboundPlaying' : ''
      } ${inBattle ? 'coreboundBattlePage' : ''} ${
        game.phase === 'won' ? 'coreboundVictoryPage' : ''
      } ${
        game.phase === 'won' && victorySequenceComplete
          ? 'coreboundVictoryCelebrating'
          : ''
      } ${game.phase === 'lost' ? 'coreboundDefeatPage' : ''} ${
        boardActive ? 'has-board-hud' : ''
      }`}
    >
      <HealingFeedback
        game={
          healthDisplay ? { ...game, hp: healthDisplay.hp ?? game.hp } : game
        }
        maxHp={healthDisplay?.maxHp ?? maxHp}
        reduceMotion={reduceMotion}
        suspended={
          !!landingHealing ||
          game.phase.startsWith('boss') ||
          protocolPending ||
          encounterSequenceActive ||
          MINI_BOSS_PHASES.includes(game.phase) ||
          GUARDIAN_PHASES.includes(game.phase)
        }
      />
      {game.phase !== 'lost' && (
        <nav className="coreboundTopbar" aria-label="Corebound navigation">
          <CoreboundNavButton to="/single-player">← Games</CoreboundNavButton>
          <span className="coreboundTopbarName">SLASH ROLL / COREBOUND</span>
          <div className="coreboundTopbarActions">
            <HapticsToggle />
            <CoreboundNavButton
              type="button"
              className="coreboundQuitButton"
              aria-haspopup="dialog"
              aria-expanded={quitDialogOpen}
              onClick={() => setQuitDialogOpen(true)}
            >
              Quit
            </CoreboundNavButton>
            <CoreboundNavButton
              ref={guideButtonRef}
              className="coreboundGuideButton"
              aria-label="Open Corebound game guide"
              aria-haspopup="dialog"
              onClick={() => setGuideQuery('')}
            >
              Info
            </CoreboundNavButton>
            <CoreboundNavButton to="/">Home</CoreboundNavButton>
          </div>
        </nav>
      )}
      {quitDialogOpen && (
        <dialog
          ref={quitDialogRef}
          className={`coreboundQuitDialog ${quitPopupClass}`}
          aria-labelledby="coreboundQuitDialogTitle"
          onCancel={(event) => {
            event.preventDefault();
            closeQuitDialog();
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) closeQuitDialog();
          }}
        >
          <span className="coreboundPanelKicker">RUN CONTROL / COREBOUND</span>
          <h2 id="coreboundQuitDialogTitle">Leave this run?</h2>
          <p>
            Quit returns to the game home. New Game resets this run and returns
            to the Corebound start screen. Your best score stays saved.
          </p>
          <div className="coreboundQuitDialogActions">
            <button
              type="button"
              className="coreboundQuitDialogQuit"
              onClick={() => closeQuitDialog(quitRun)}
            >
              Quit
            </button>
            <button
              type="button"
              className="coreboundQuitDialogNewGame"
              onClick={() => closeQuitDialog(startNewGame)}
            >
              New Game
            </button>
            <button
              ref={quitCancelRef}
              type="button"
              className="coreboundQuitDialogCancel"
              onClick={closeQuitDialog}
            >
              Cancel
            </button>
          </div>
        </dialog>
      )}
      {game.phase !== 'lost' && guideQuery !== null && (
        <GameGuide
          relics={game.relics}
          initialQuery={guideQuery}
          onClose={() => setGuideQuery(null)}
          returnFocusRef={guideButtonRef}
        />
      )}

      {!['intro', 'opening', 'lost'].includes(game.phase) && (
        <RunStatus
          game={game}
          display={healthDisplay}
          cinematic={rewardHudActive}
          healthRef={rewardHealthRef}
          shardsRef={rewardShardsRef}
          reduceMotion={reduceMotion}
          boardActive={boardActive}
          hideStats={hideBoardStats}
        />
      )}
      <div
        className={`coreboundLayout ${
          ['ready', 'minibossReady', 'guardianReady'].includes(game.phase)
            ? 'coreboundLayoutRollSetup'
            : ''
        } ${
          sidebarOpen
            ? 'coreboundLayoutSidebarOpen'
            : 'coreboundLayoutSidebarClosed'
        }`}
      >
        <section className="coreboundMain" ref={mainRef}>
          {game.phase === 'intro' && (
            <div className="coreboundHeading">
              <span className="coreboundEyebrow">
                A SINGLE-PLAYER DICE ADVENTURE
              </span>
              <h1>Corebound</h1>
              <p>
                Roll your dice, collect helpful items, and battle through ten
                areas to stop the Glitch Core from destroying the galaxy.
              </p>
            </div>
          )}

          {game.phase === 'intro' && (
            <div className="coreboundPanel coreboundIntro">
              <span className="coreboundPanelKicker">
                THE PORTAL NETWORK IS UNSTABLE
              </span>
              <h2>Can you reach the Core?</h2>
              <p>
                Your last working portal weapon is the Rift Loom. Launch a
                charged die across its tiles to strike the Core’s minions,
                recover their caches, and reopen the galaxy network. A 500 HP
                Warden guards Sector 5. The Core waits in Sector 10.
              </p>
              <SparkButton
                onClick={() => {
                  playSound('/sounds/transition-to-next-page.mp3');
                  setGame(startRun);
                }}
              >
                Start a run →
              </SparkButton>
              {(game.bestScore > 0 || game.victories > 0) && (
                <p className="coreboundRecord">
                  Best score: {game.bestScore} · Victories: {game.victories}
                </p>
              )}
            </div>
          )}

          {game.phase === 'opening' && (
            <CoreboundOpening
              dieId={game.selectedDie}
              reduceMotion={reduceMotion}
              onComplete={finishOpening}
            />
          )}

          {game.phase === 'relic' && (
            <div className="coreboundPanel">
              <span className="coreboundPanelKicker">
                SECTOR {game.floor} / {TOTAL_SECTORS}
              </span>
              <div className="coreboundRelicOfferHeading">
                <h2>Choose a relic</h2>
                <button
                  className="coreboundRelicRerollButton"
                  type="button"
                  onClick={rerollRelics}
                  disabled={game.shards < RELIC_REROLL_COST}
                >
                  Reroll Relics · {RELIC_REROLL_COST} shards
                </button>
              </div>
              <p>
                Choose one relic. Its effect lasts for the rest of this run.
              </p>
              <div className="coreboundCardGrid">
                {getRelicOptions(game).map((id) => (
                  <button
                    key={id}
                    className={`coreboundChoice coreboundRelicChoice coreboundRarityCard-${(
                      RELICS[id].category || RELICS[id].rarity
                    ).toLowerCase()}`}
                    onClick={() => takeRelic(id)}
                  >
                    {renderRelicCard(id, 'Take relic →')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {game.phase === 'shop' && (
            <CoreAccess
              game={game}
              reduceMotion={reduceMotion}
              onEnter={() => {
                setGame(beginBoss);
                setAcquiredRelic(null);
                setRepairFeedback(null);
              }}
            >
              <div className="coreboundPanelHeadingRow">
                <span className="coreboundPanelKicker">
                  SECTOR {CORE_SECTOR} / CORE ACCESS
                </span>
                <BoardConditionNotice
                  condition={getRoomBoardCondition(game, 'boss')}
                  inline
                  shared
                />
              </div>
              <h2 id="coreAccessMarketTitle">Final Signal Exchange</h2>
              <p>
                {game.marketPurchased === game.floor
                  ? 'Your relic purchase is complete. Healing stays open until you enter the Core arena.'
                  : game.portalRun
                  ? 'Your last shop before the Core. Buy one helpful item, then use any remaining shards to heal.'
                  : 'Choose one of these four helpful items, then heal before starting the fight.'}
              </p>
              <RunStatus
                game={game}
                reduceMotion={reduceMotion}
                hideStats
                inShop
              />
              <button
                className="coreboundRepair"
                title={REPAIR_DESCRIPTION}
                onClick={handleRepair}
                disabled={game.shards < repairCost}
              >
                <RepairButtonAura />
                <DoorIcon kind="Recovery" /> Heal 3 HP · {repairCost} shards
              </button>
              <p className="coreboundRepairHint">
                Each extra heal here costs 2 more shards.
              </p>
              {repairFeedback ? (
                <div
                  key={repairFeedback.key}
                  className="coreboundRepairFeedback"
                  role="status"
                >
                  <span>+</span>
                  <strong>HEAL COMPLETE</strong>
                  <small>+ {repairFeedback.healed} HP</small>
                  {repairFeedback.maxHpGained > 0 && (
                    <small>
                      Maximum increased by {repairFeedback.maxHpGained}
                    </small>
                  )}
                </div>
              ) : null}
              <RelicStoreOffers
                className="coreboundShopGrid"
                offers={(game.shopOffers || []).filter(
                  (id) =>
                    !game.relics.includes(id) && !shopExitingIds.includes(id)
                )}
                purchased={game.marketPurchased === game.floor}
                selectedId={purchaseReceipt?.id}
                reduceMotion={reduceMotion}
                isDisabled={(id) => game.shards < getRelicPrice(game, id)}
                onBuy={buyBossUpgrade}
                purchaseFeedback={
                  purchaseReceipt && (
                    <RelicActivation
                      key={purchaseReceipt.id}
                      activation={purchaseReceipt}
                      label="RELIC ACQUIRED"
                      showDescription
                      reduceMotion={reduceMotion}
                    />
                  )
                }
                renderOffer={(id) => (
                  <>
                    {renderRelicCard(
                      id,
                      `${getRelicPrice(game, id)} shards · Buy`,
                      BOSS_RELIC_NOTES[id],
                      'BOSS UPGRADE'
                    )}
                    <span className="coreboundShopTier">
                      {getShopTier(id, game)}
                    </span>
                  </>
                )}
              />
              <p className="coreboundHint">
                The Core has {BOSS_MAX_HP} HP. Every attack chips it down.
                Choose Strike, Guard, or Overload to change your die and
                defenses.
              </p>
              <p className="coreboundHint coreboundShopWarning">
                <CoreboundIcon
                  name="signalWarning"
                  className="coreboundShopWarningIcon"
                />
                Purchases and healing close when the fight starts.
                <CoreboundIcon
                  name="signalWarning"
                  className="coreboundShopWarningIcon"
                />
              </p>
            </CoreAccess>
          )}

          {GUARDIAN_PHASES.includes(game.phase) && (
            <GuardianBattle
              game={game}
              setGame={setGame}
              reduceMotion={reduceMotion}
              healthRef={rewardHealthRef}
              shardsRef={rewardShardsRef}
              onRewardProgress={setRewardProgress}
              onVictoryPresented={setCompletedEncounterResult}
              onCombatStage={setCombatPresentation}
            />
          )}

          {inBattle && (
            <BossBattle
              game={game}
              setGame={setGame}
              reduceMotion={reduceMotion}
              onOpenGuide={setGuideQuery}
            />
          )}

          {game.phase === 'choose' && game.portalRun && (
            <PortalChamber
              game={game}
              reduceMotion={reduceMotion}
              onSelect={(id) => selectRoom(id, { entrySound: false })}
              onRepair={handleRepair}
              repairCost={repairCost}
              repairFeedback={repairFeedback}
              onUnlockSeen={() =>
                setGame((current) =>
                  current.phase === 'choose' &&
                  current.portalRoutes?.floor === current.floor
                    ? {
                        ...current,
                        portalRoutes: {
                          ...current.portalRoutes,
                          unlockSeen: true,
                        },
                      }
                    : current
                )
              }
              marketContent={
                <div className="coreboundPanel coreboundRunMarket">
                  <div className="coreboundRunMarketHeading">
                    <div>
                      <span className="coreboundPanelKicker">
                        SIGNAL EXCHANGE
                      </span>
                      <h2 id="coreboundRunMarketTitle">
                        {game.marketPurchased === game.floor
                          ? 'Relic added'
                          : 'Put your shards to work'}
                      </h2>
                    </div>
                  </div>
                  <p>
                    {game.marketPurchased === game.floor
                      ? 'Your relic is ready to help. Heal or save your remaining shards, then choose a portal.'
                      : game.floor === 2
                      ? 'Pick a FREE Common relic, or buy another item. You can take one item this visit. Relics help you for the rest of this adventure; shards are the coins you spend here.'
                      : 'Buy one helpful item, heal, or save your shards for later. Relics help you for the rest of this adventure.'}
                  </p>
                  <RunStatus
                    game={game}
                    reduceMotion={reduceMotion}
                    hideStats
                    inShop
                  />
                  <button
                    type="button"
                    className="coreboundRepair"
                    title={REPAIR_DESCRIPTION}
                    onClick={handleRepair}
                    disabled={game.shards < repairCost}
                  >
                    <RepairButtonAura />
                    <DoorIcon kind="Recovery" /> Heal 3 HP · {repairCost} shards
                  </button>
                  <p className="coreboundRepairHint">
                    Each extra heal here costs 2 more shards.
                  </p>
                  {repairFeedback && (
                    <p role="status">
                      {`Restored ${repairFeedback.healed} HP.${
                        repairFeedback.maxHpGained
                          ? ` Maximum grew by ${repairFeedback.maxHpGained}.`
                          : ''
                      }`}
                    </p>
                  )}
                  {game.relicOffers && (
                    <RelicStoreOffers
                      offers={game.relicOffers}
                      purchased={game.marketPurchased === game.floor}
                      selectedId={purchaseReceipt?.id}
                      reduceMotion={reduceMotion}
                      isDisabled={(id) => game.shards < getRelicPrice(game, id)}
                      onBuy={takeRelic}
                      purchaseFeedback={
                        purchaseReceipt && (
                          <RelicActivation
                            key={purchaseReceipt.id}
                            activation={purchaseReceipt}
                            label="RELIC ACQUIRED"
                            showDescription
                            reduceMotion={reduceMotion}
                          />
                        )
                      }
                      renderOffer={(id) =>
                        renderRelicCard(
                          id,
                          getRelicPrice(game, id) === 0
                            ? 'FREE · Take relic'
                            : `${getRelicPrice(game, id)} shard${
                                getRelicPrice(game, id) === 1 ? '' : 's'
                              } · Buy`
                        )
                      }
                    />
                  )}
                </div>
              }
            />
          )}

          {game.phase === 'choose' && !game.portalRun && (
            <div className="coreboundPanel">
              <span className="coreboundPanelKicker">
                SECTOR {game.floor} / {TOTAL_SECTORS}
              </span>
              <h2>
                {game.floor === CORE_SECTOR
                  ? 'The Core is awake'
                  : game.floor === 5
                  ? 'The Rift Warden'
                  : 'Choose your door'}
              </h2>
              <p>
                {game.floor === CORE_SECTOR
                  ? 'Defeat the Core to escape. A miss hurts, but you can retry if you survive.'
                  : game.floor === 5
                  ? 'One guardian guards this sector. Defeat its 500 HP shell to reach Sector 6.'
                  : 'Compare enemy health, how hard it hits back, and the rewards before choosing.'}
              </p>
              {acquiredRelic && (
                <div className="coreboundRelicAcquired" role="status">
                  <RelicActivation
                    activation={acquiredRelic}
                    label="RELIC ACQUIRED"
                    showDescription
                    reduceMotion={reduceMotion}
                  />
                </div>
              )}
              <div className="coreboundCardGrid">
                {getRooms(game)
                  .filter((_, index) => game.floor !== 5 || index === 0)
                  .map((room) =>
                    game.floor === 5
                      ? {
                          ...room,
                          name: 'The Rift Warden',
                          kind: 'Trial',
                          target: 500,
                          damage: 2,
                          shards: 10,
                          heal: 0,
                          bonusRelic: true,
                          description:
                            'Defeat the 500 HP Warden. Fewer attacks earn more shards; victory always grants a random relic.',
                        }
                      : { ...room, damage: room.damage <= 2 ? 1 : 2 }
                  )
                  .map((item) => (
                    <button
                      key={item.id}
                      className={`coreboundChoice coreboundDoor coreboundDoor-${
                        getDoorType(item.kind)?.id || 'boss'
                      }`}
                      style={{ '--door-color': getDoorType(item.kind)?.color }}
                      onClick={() => selectRoom(item.id)}
                    >
                      <span className="coreboundChoiceType">
                        <DoorIcon kind={item.kind} />
                        {item.kind.toUpperCase()}
                      </span>
                      <strong>{item.name}</strong>
                      <span>{item.description}</span>
                      <span className="coreboundRoomOdds">
                        Minion {item.target} HP · Retaliation: −{item.damage} HP
                      </span>
                      <span className="coreboundRoomReward">
                        Win: {getDoorReward(item)}
                      </span>
                      <BoardConditionBadge
                        condition={getRoomBoardCondition(game, item.id)}
                      />
                      <span className="coreboundChoiceAction">
                        Choose this door →
                      </span>
                    </button>
                  ))}
              </div>
              <button
                className="coreboundRepair"
                title={REPAIR_DESCRIPTION}
                onClick={handleRepair}
                disabled={game.shards < repairCost}
              >
                <RepairButtonAura />
                <DoorIcon kind="Recovery" /> Heal 3 HP · {repairCost} shards
              </button>
              <p className="coreboundRepairHint">
                Each extra heal here costs 2 more shards.
              </p>
              {repairFeedback ? (
                <motion.div
                  key={repairFeedback.key}
                  className="coreboundRepairFeedback"
                  role="status"
                  initial={reduceMotion ? false : { opacity: 0, scale: 0.7 }}
                  animate={
                    reduceMotion
                      ? {}
                      : { opacity: 1, scale: [0.7, 1.12, 1], y: [8, -4, 0] }
                  }
                  transition={{ duration: 0.55, ease: 'easeOut' }}
                >
                  <span aria-hidden="true">+</span>
                  <strong>HEAL COMPLETE</strong>
                  <small>+ {repairFeedback.healed} HP</small>
                  {repairFeedback.maxHpGained > 0 && (
                    <small>
                      Maximum increased by {repairFeedback.maxHpGained}
                    </small>
                  )}
                </motion.div>
              ) : null}
            </div>
          )}

          {game.phase === 'ready' && room && (
            <RollReveal
              game={game}
              room={room}
              effectiveRoll={null}
              setup
              arrival
              onSelectDie={(id) => setGame((current) => selectDie(current, id))}
              onStartRoll={startRoomRoll}
              onMarkBounty={(index) =>
                setGame((current) => markBountyTile(current, index))
              }
              onClearBounty={() => setGame(clearBountyTag)}
              onToggleChainReaction={() => setGame(toggleChainReaction)}
              onResolve={() => {}}
            />
          )}

          {game.phase === 'rolled' &&
            room &&
            !showRollReveal &&
            game.roll === null && (
              <div className="coreboundPanel coreboundReadyPanel">
                <span className="coreboundPanelKicker">ROLL COMPLETE</span>
                <h2>Your roll is waiting</h2>
                <p>
                  The dice is ready to reveal its landing face for {room.name}.
                </p>
                <button
                  className="coreboundPrimary"
                  onClick={() => {
                    playSound('/sounds/whoosh-transition-sound.mp3');
                    setShowRollReveal(true);
                  }}
                >
                  Reveal the roll →
                </button>
              </div>
            )}

          {game.phase === 'rolled' &&
            room &&
            !game.lastStandProtocol?.pending &&
            (showRollReveal || game.roll !== null) && (
              <RollReveal
                game={game}
                room={room}
                effectiveRoll={effectiveRoll}
                restoreCompletedRoll={restoreCompletedRoll}
                rollCharge={rollCharge}
                rollLaunch={rollLaunch}
                onRollComplete={finishRoomRoll}
                onPhaseAnchor={() => setGame(activatePhaseAnchor)}
                onPortalTransit={() => setGame(activatePortalPair)}
                onBoardBump={() =>
                  setGame((current) => recordThrowEvent(current, 'bump'))
                }
                onFlip={() => setGame(flipRoll)}
                onBoardTileChoice={(choice) =>
                  setGame((current) => resolveBoardTileChoice(current, choice))
                }
                onResolve={() => {
                  const didPass =
                    game.breachIntro || effectiveRoll >= room.target;
                  if (!didPass) playSound('/sounds/dice-hits-board.mp3');
                  setGame(resolveRoll);
                }}
              />
            )}

          {(game.phase === 'result' ||
            game.phase === 'won' ||
            (game.phase === 'lost' && defeatSequenceComplete)) &&
            game.lastResult && (
              <motion.div
                className={`coreboundPanel coreboundOutcome ${
                  game.breachIntro
                    ? 'coreboundIntroOutcome'
                    : game.lastResult.success
                    ? 'coreboundSuccess'
                    : 'coreboundFailure'
                }`}
                initial={
                  reduceMotion ? false : { opacity: 0, y: 24, scale: 0.96 }
                }
                animate={
                  reduceMotion
                    ? {}
                    : {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        x:
                          game.breachIntro || game.lastResult.success
                            ? 0
                            : [0, -9, 7, -4, 0],
                      }
                }
                transition={{ duration: 0.35, ease: 'easeOut' }}
              >
                <div className="coreboundOutcomeBody">
                  {game.phase !== 'won' && (
                    <span className="coreboundPanelKicker">
                      SECTOR {game.floor} / {TOTAL_SECTORS}
                    </span>
                  )}
                  {game.phase === 'won' ? (
                    <Victory
                      game={game}
                      active={victorySequenceComplete}
                      reduceMotion={reduceMotion}
                    />
                  ) : (
                    <h2>
                      {game.phase === 'lost'
                        ? 'Signal severed'
                        : game.breachIntro
                        ? 'Starting reward secured'
                        : game.lastResult.protocolSaved
                        ? 'Last Stand Protocol holds!'
                        : game.lastResult.success
                        ? game.lastResult.miniBoss
                          ? 'The Rift Warden is defeated!'
                          : 'Guardian defeated!'
                        : 'The minion strikes back'}
                    </h2>
                  )}
                  {game.phase === 'lost' && (
                    <>
                      <p className="coreboundDefeatMessage">
                        {game.lastResult.boss
                          ? `The Core silenced your signal in round ${game.lastResult.round}.`
                          : `${game.lastResult.roomName} overwhelmed your signal.`}
                      </p>
                      {game.newBestScore && (
                        <div
                          className="coreboundBestScoreHighlight coreboundDefeatRecordBanner"
                          role="status"
                        >
                          <span>NEW PERSONAL BEST</span>
                          <strong>{game.score}</strong>
                          <small>Your run set a new record.</small>
                        </div>
                      )}
                      <div
                        className="coreboundDefeatRecap"
                        role="group"
                        aria-label="Run summary"
                      >
                        <div>
                          <span>SECTOR REACHED</span>
                          <strong>
                            {game.floor} / {TOTAL_SECTORS}
                          </strong>
                        </div>
                        <div>
                          <span>SHARDS CARRIED</span>
                          <strong>{game.shards}</strong>
                        </div>
                        {game.lastResult.boss && (
                          <div>
                            <span>CORE ROUND</span>
                            <strong>{game.lastResult.round}</strong>
                          </div>
                        )}
                        {game.lastResult.boss && (
                          <div>
                            <span>BIGGEST ATTACK</span>
                            <strong>{game.runBestAttack || 0}</strong>
                          </div>
                        )}
                        <div>
                          <span>RUN SCORE</span>
                          <strong>{game.score}</strong>
                        </div>
                        <div>
                          <span>PERSONAL BEST</span>
                          <strong>{game.bestScore}</strong>
                        </div>
                      </div>
                    </>
                  )}
                  {!game.lastResult.boss && (
                    <p
                      className={game.phase === 'lost' ? 'coreboundSrOnly' : ''}
                    >
                      {game.breachIntro
                        ? 'The Breach is a safe introduction. Your landing reward is yours; choose a portal when you are ready.'
                        : game.lastResult.guardian
                        ? `${game.lastResult.enemy.name} defeated in ${
                            game.lastResult.attacks
                          } attack${
                            game.lastResult.attacks === 1 ? '' : 's'
                          }. Final strike: ${
                            game.lastResult.attackDealt
                          } damage.`
                        : `${
                            game.lastResult.enemy?.name ||
                            game.lastResult.roomName
                          }: your attack dealt ${formatDieValue(
                            game.lastResult.effectiveRoll
                          )} against ${game.lastResult.target} HP.`}
                    </p>
                  )}
                  <GuardianDamageCalculation
                    key={game.lastResult.combatId}
                    result={game.lastResult}
                  />
                  {game.lastResult.boardLanding &&
                    !game.lastResult.damageCalculation && (
                      <div className="coreboundBoardResult" role="status">
                        <span>LANDED ON BOARD SQUARE</span>
                        <strong>
                          {game.lastResult.boardLanding.effect?.type ===
                          'relic' ? (
                            <TreasureChestIcon />
                          ) : game.lastResult.boardLanding.effect?.type ===
                            'shield' ? (
                            `${game.lastResult.boardLanding.effect.amount}`
                          ) : (
                            getBoardSquareImpactLabel(
                              game.lastResult.boardLanding
                            )
                          )}
                        </strong>
                        {game.lastResult.boardLanding.effect && (
                          <small>
                            {game.lastResult.boardLanding.effect.type === 'heal'
                              ? `+ ${game.lastResult.boardLanding.effect.amount} Max HP`
                              : game.lastResult.boardLanding.effect.type ===
                                'shield'
                              ? `Blocked ${
                                  game.lastResult.boardShieldBlocked || 0
                                } damage`
                              : game.lastResult.boardLanding.effect.type ===
                                'shards'
                              ? `Collected ${game.lastResult.boardLanding.effect.amount} shards`
                              : game.lastResult.boardLanding.effect.type ===
                                'relic'
                              ? `Acquired ${
                                  RELICS[
                                    game.lastResult.boardTreasureReward?.id
                                  ]?.name || 'a relic'
                                }`
                              : game.lastResult.boardLanding.effect.type ===
                                'critical'
                              ? 'Critical hit: roll doubled'
                              : Number.isFinite(
                                  game.lastResult.boardLanding.effect.amount
                                )
                              ? `Took ${game.lastResult.boardLanding.effect.amount} hazard damage`
                              : 'Board effect activated'}
                          </small>
                        )}
                      </div>
                    )}
                  {game.lastResult.bonusRelic && (
                    <div className="coreboundRelicAcquired" role="status">
                      <RelicActivation
                        activation={{
                          id: game.lastResult.bonusRelic,
                          message: `${
                            RELICS[game.lastResult.bonusRelic].name
                          } added to your relics`,
                        }}
                        label="BONUS RELIC ACQUIRED"
                        showDescription
                        reduceMotion={reduceMotion}
                      />
                    </div>
                  )}
                  {game.phase !== 'lost' &&
                    game.phase !== 'won' &&
                    game.lastResult.relicActivations?.length > 0 && (
                      <div
                        className="coreboundRelicFeedbackStack"
                        role="status"
                      >
                        {game.lastResult.relicActivations.map(
                          (activation, index) => (
                            <RelicActivation
                              key={`${activation.id}-${index}`}
                              activation={activation}
                              index={index}
                              reduceMotion={reduceMotion}
                            />
                          )
                        )}
                      </div>
                    )}
                  {game.phase === 'result' && (
                    <>
                      {game.portalRun && (
                        <div className="coreboundRouteResult" role="status">
                          <strong>
                            {game.breachIntro
                              ? 'Starter reward collected'
                              : game.floor === TOTAL_SECTORS - 1
                              ? 'Core access found'
                              : game.floor === 4
                              ? 'Warden gate located'
                              : getRouteOutcome(game.lastResult) === 'strong'
                              ? 'First-strike victory — hard portal unlocked free'
                              : game.lastResult.success
                              ? 'Two portal routes stabilized'
                              : 'Recovery routes found'}
                          </strong>
                          {game.lastResult.earned > 0
                            ? `Collected ${game.lastResult.earned} shard${
                                game.lastResult.earned === 1 ? '' : 's'
                              }. `
                            : ''}
                          {game.breachIntro
                            ? 'Choose a portal to begin Sector 2.'
                            : game.floor === 4
                            ? 'The 500 HP Rift Warden is the only guardian in Sector 5.'
                            : game.floor < TOTAL_SECTORS - 1
                            ? `Your next destination is yours to choose.${
                                getRouteOutcome(game.lastResult) !== 'strong'
                                  ? ' Hard portal entry costs 3 shards.'
                                  : ''
                              }`
                            : 'The final market and the Core are ahead.'}
                        </div>
                      )}
                    </>
                  )}
                  {(game.phase === 'won' || game.phase === 'lost') && (
                    <>
                      {game.phase === 'won' ? (
                        <div
                          className="coreboundScoreGrid"
                          role="table"
                          aria-label="Run and all-time records"
                        >
                          <div className="coreboundScoreGridHeader" role="row">
                            <span role="columnheader">RECORD</span>
                            <span role="columnheader">THIS RUN</span>
                            <span role="columnheader">BEST EVER</span>
                          </div>
                          <div className="coreboundScoreGridRow" role="row">
                            <strong role="rowheader">Overall score</strong>
                            <span role="cell">{game.score}</span>
                            <b role="cell">{game.bestScore}</b>
                          </div>
                          <div className="coreboundScoreGridRow" role="row">
                            <strong role="rowheader">Highest attack</strong>
                            <span role="cell">{game.runBestAttack || 0}</span>
                            <b role="cell">{game.bestAttack || 0}</b>
                          </div>
                        </div>
                      ) : null}
                      {!!game.lastResult.scoreBreakdown?.length && (
                        <div className="coreboundScoreBreakdown">
                          <strong>Score breakdown</strong>
                          {game.lastResult.scoreBreakdown.map((item) => (
                            <div key={item.label}>
                              <span>
                                {item.label}
                                {item.detail ? ` (${item.detail})` : ''}
                              </span>
                              <b>+{item.points}</b>
                            </div>
                          ))}
                          <div className="coreboundScoreBreakdownTotal">
                            <span>Total</span>
                            <b>{game.score}</b>
                          </div>
                        </div>
                      )}
                      {game.phase === 'won' && (
                        <div className="coreboundBestScoreHighlight">
                          <span>
                            {game.newBestScore
                              ? 'NEW PERSONAL BEST'
                              : 'BEST OVERALL SCORE'}
                          </span>
                          <strong>{game.bestScore}</strong>
                          <small>Corebreaker record</small>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="coreboundOutcomeActions">
                  {game.phase === 'result' && (
                    <button
                      className="coreboundPrimary coreboundNextFloorButton"
                      onClick={() => {
                        playSound('/sounds/transition-to-next-page.mp3');
                        setGame((current) =>
                          migrateGuardianGame(continueRun(current))
                        );
                      }}
                    >
                      {game.portalRun && (
                        <GiVortex
                          className="coreboundPortalButtonIcon"
                          aria-hidden="true"
                          focusable="false"
                        />
                      )}
                      {game.portalRun
                        ? game.breachIntro
                          ? 'Choose your first portal →'
                          : game.floor === MINI_BOSS_SECTOR &&
                            !game.miniBoss?.defeated &&
                            !game.guardian?.defeated
                          ? 'Face the Rift Warden · 500 HP →'
                          : game.floor === TOTAL_SECTORS - 1
                          ? 'Reach the Core market →'
                          : 'Open portal routes →'
                        : game.floor === CORE_SECTOR
                        ? 'Retry the Core →'
                        : 'Next sector →'}
                    </button>
                  )}
                  {(game.phase === 'won' || game.phase === 'lost') &&
                    (game.phase === 'lost' ? (
                      <div className="coreboundDefeatActions">
                        <SparkButton
                          className="coreboundNewRunButton"
                          onClick={startNewRun}
                        >
                          Try again →
                        </SparkButton>
                        <Link to="/" className="coreboundDefeatHomeLink">
                          Return home
                        </Link>
                      </div>
                    ) : (
                      <SparkButton
                        className="coreboundNewRunButton"
                        onClick={startNewRun}
                      >
                        New run →
                      </SparkButton>
                    ))}
                </div>
              </motion.div>
            )}
        </section>

        {!['intro', 'opening', 'lost'].includes(game.phase) && (
          <aside
            id="coreboundSidebarContent"
            className={`coreboundSidebar ${sidebarOpen ? '' : 'is-collapsed'}`}
          >
            <button
              type="button"
              className="coreboundSidebarToggle"
              aria-label={
                sidebarOpen
                  ? 'Hide run details'
                  : 'Show your relics and recent encounters'
              }
              aria-expanded={sidebarOpen}
              aria-controls="coreboundSidebarContent"
              title={
                sidebarOpen
                  ? 'Hide run details'
                  : 'Your relics and recent encounters'
              }
              onClick={() => setSidebarOpen((open) => !open)}
            >
              <span className="coreboundSidebarToggleArrow" aria-hidden="true">
                {sidebarOpen ? '›' : '‹'}
              </span>
              {!sidebarOpen && (
                <span
                  className="coreboundSidebarToggleLabel"
                  aria-hidden="true"
                >
                  RELICS
                </span>
              )}
            </button>
            <details
              className="coreboundStatPanel coreboundSidebarSection"
              open={window.innerWidth <= 600 || window.innerWidth >= 1100}
            >
              <summary className="coreboundPanelKicker">
                YOUR RELICS <span>{game.relics.length}</span>
              </summary>
              <div className="coreboundSidebarBody">
                {game.relics.length === 0 ? (
                  <p>No relics yet.</p>
                ) : (
                  game.relics.map((id) => (
                    <div
                      key={id}
                      className={`coreboundRelic ${
                        id === 'lastStandProtocol' &&
                        game.lastStandProtocol?.used
                          ? 'coreboundProtocolConsumed'
                          : ''
                      }`}
                    >
                      <strong>{RELICS[id].name}</strong>
                      <small
                        className={`coreboundRarity-${RELICS[
                          id
                        ].category.toLowerCase()}`}
                      >
                        {RELICS[id].category} · OWNED
                      </small>
                      {id === 'lastStandProtocol' &&
                        game.lastStandProtocol?.used && (
                          <small>CONSUMED · SURVIVAL ATTEMPT SPENT</small>
                        )}
                      {RELICS[id].healOncePerRun &&
                        game.relicHealingUses?.[id] && (
                          <small>USED · CHARGE SPENT THIS RUN</small>
                        )}
                      <span>
                        {game.floor === CORE_SECTOR
                          ? BOSS_RELIC_NOTES[id] || RELICS[id].description
                          : RELICS[id].description}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </details>
            {game.history.length > 0 && (
              <details
                className="coreboundStatPanel coreboundSidebarSection"
                open={window.innerWidth <= 600 || window.innerWidth >= 1100}
              >
                <summary className="coreboundPanelKicker">
                  RECENT ENCOUNTERS
                </summary>
                <div className="coreboundSidebarBody">
                  {game.history.map((entry, index) => (
                    <p
                      className="coreboundHistory"
                      key={`${entry.roomName}-${index}`}
                    >
                      {entry.boss
                        ? game.phase === 'won'
                          ? '✓'
                          : game.phase === 'lost'
                          ? '✕'
                          : '…'
                        : entry.success
                        ? '✓'
                        : '✕'}{' '}
                      {entry.roomName}
                      {entry.boss && entry.round
                        ? ` · Round ${entry.round}`
                        : ''}
                    </p>
                  ))}
                </div>
              </details>
            )}
          </aside>
        )}
      </div>
      {defeatSequenceActive && (
        <DefeatSequence
          dieId={game.selectedDie}
          faceValues={game.powerUp?.faceValues}
          rollValue={game.lastResult?.naturalRoll ?? game.roll}
          bossRound={game.lastResult?.boss ? game.lastResult.round : null}
          floor={game.floor}
          reduceMotion={reduceMotion}
          onComplete={() => setCompletedDefeatResult(game.lastResult)}
        />
      )}
      {victorySequenceActive && (
        <VictorySequence
          dieId={victoryDie.dieId}
          faceValues={victoryDie.faceValues}
          onComplete={() => setCompletedVictoryResult(game.lastResult)}
        />
      )}
      {encounterSequenceActive && (
        <EncounterLootSequence
          key={game.floor + ':' + game.lastResult.roomId}
          result={game.lastResult}
          reduceMotion={reduceMotion}
          healthRef={rewardHealthRef}
          shardsRef={rewardShardsRef}
          onRewardProgress={setRewardProgress}
          onComplete={() => {
            setCompletedEncounterResult(game.lastResult);
            if (game.lastResult.training) setGame(continueRun);
          }}
        />
      )}
      {game.lastStandProtocol?.pending && (
        <LastStandProtocolRoll
          game={game}
          setGame={setGame}
          onResolve={() =>
            setGame((current) => {
              const resolved = resolveLastStandRoll(current);
              if (resolved === current) return current;
              const context = current.lastStandProtocol.pending.context;
              return resolved.guardian?.active
                ? startGuardianAttack(resolved)
                : context === 'room'
                ? resolveRoll(resolved)
                : context === 'miniboss'
                ? resolveMiniBossAttack(resolved)
                : resolved;
            })
          }
        />
      )}
    </main>
  );
};

const CoreboundWithHelp = () => (
  <CoreboundHelpProvider>
    <Corebound />
  </CoreboundHelpProvider>
);

export default CoreboundWithHelp;
